/**
 * HiringPredictionCard - Displays ripple-logic hiring predictions
 * 
 * For staffing agencies: Shows expected hires, target roles, and timing
 * based on funding/expansion signals.
 * 
 * DEX Standards:
 * - Graphics > Words: Large numbers, minimal text
 * - 5-Word Filter: Concise labels
 * - Playfair Display: Typography
 * - 700ms transitions: Smooth animations
 */

import React from 'react';
import { TrendingUp, Clock, Users, Briefcase } from 'lucide-react';
import { DemandZoneBadge } from './DemandZoneBadge';

interface HiringPrediction {
  stage?: string;
  stageLabel?: string;
  amount?: number;
  amountFormatted?: string;
  expectedHires?: { min: number; max: number };
  targetDepartments?: string[];
  targetTitles?: string[];
  demandZone?: string;
}

interface OutreachTiming {
  daysSinceEvent?: number;
  daysRemaining?: number;
  optimalWindow?: string;
  urgency?: 'HIGH' | 'MEDIUM' | 'LOW';
  reasoning?: string;
}

interface HiringPredictionCardProps {
  prediction: HiringPrediction | null;
  timing?: OutreachTiming | null;
  fundingAmount?: string | null;
  compact?: boolean;
  onFindDecisionMakers?: () => void;
  onFindTalent?: () => void;
}

export function HiringPredictionCard({
  prediction,
  timing,
  fundingAmount,
  compact = false,
  onFindDecisionMakers,
  onFindTalent,
}: HiringPredictionCardProps) {
  if (!prediction) return null;

  const { expectedHires, targetTitles, targetDepartments, demandZone, stageLabel, amountFormatted } = prediction;
  const urgency = timing?.urgency || demandZone?.toUpperCase() || 'MEDIUM';

  if (compact) {
    // Compact but informative version for inline display
    return (
      <div className="p-3 bg-gradient-to-r from-[var(--eq-purple)]/5 to-[var(--eq-teal)]/5 rounded-xl transition-all duration-700 border border-black/5">
        {/* Top row: Hiring prediction + urgency badge */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[var(--eq-purple)]" />
            {expectedHires && (
              <span 
                className="text-sm font-bold text-[var(--eq-purple)]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {expectedHires.min}-{expectedHires.max} hires expected
              </span>
            )}
          </div>
          {urgency && <DemandZoneBadge zone={urgency} size="sm" />}
        </div>
        
        {/* Target roles - show up to 3 */}
        {targetTitles && targetTitles.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {targetTitles.slice(0, 3).map((title, idx) => (
              <span 
                key={idx}
                className="px-2 py-0.5 bg-white/80 rounded text-xs text-black/60 border border-black/5"
              >
                {title}
              </span>
            ))}
            {targetTitles.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-black/40">
                +{targetTitles.length - 3} more
              </span>
            )}
          </div>
        )}
        
        {/* Timing insight */}
        {timing?.optimalWindow && (
          <div className="flex items-center gap-1.5 text-xs text-black/50">
            <Clock className="w-3 h-3" />
            <span>{timing.optimalWindow}</span>
            {timing.daysSinceEvent !== undefined && timing.daysSinceEvent <= 14 && (
              <span className="text-amber-600 font-medium ml-1">First mover advantage</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[var(--eq-purple)]/5 via-white to-[var(--eq-teal)]/5 rounded-2xl border border-black/5 overflow-hidden transition-all duration-700 hover:shadow-lg">
      {/* Header */}
      <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[var(--eq-purple)]" />
          <span 
            className="text-sm font-semibold text-black"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Hiring Prediction
          </span>
        </div>
        {urgency && <DemandZoneBadge zone={urgency} />}
      </div>

      {/* Main Stats */}
      <div className="px-5 py-4">
        {/* Expected Hires - THE BIG NUMBER */}
        {expectedHires && (
          <div className="mb-4">
            <div className="flex items-baseline gap-2">
              <span 
                className="text-4xl font-bold text-[var(--eq-purple)]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {expectedHires.min}-{expectedHires.max}
              </span>
              <span className="text-sm text-black/50">hires expected</span>
            </div>
            {stageLabel && (
              <p className="text-xs text-black/40 mt-1">
                Based on {stageLabel} {amountFormatted || fundingAmount ? `(${amountFormatted || fundingAmount})` : ''} patterns
              </p>
            )}
          </div>
        )}

        {/* Target Roles - Compact Pills */}
        {targetTitles && targetTitles.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-black/40 uppercase tracking-wider mb-2">
              Target Roles
            </p>
            <div className="flex flex-wrap gap-1.5">
              {targetTitles.slice(0, 4).map((title, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-1 bg-white rounded-lg text-xs text-black/70 border border-black/5"
                >
                  {title}
                </span>
              ))}
              {targetTitles.length > 4 && (
                <span className="px-2 py-1 text-xs text-black/40">
                  +{targetTitles.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Timing Insight */}
        {timing && (
          <div className="p-3 bg-black/[0.02] rounded-xl">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-3.5 h-3.5 text-black/40" />
              <span className="text-xs font-semibold text-black/60">
                {timing.optimalWindow || 'Optimal timing unknown'}
              </span>
            </div>
            {timing.reasoning && (
              <p className="text-xs text-black/40 ml-5.5">
                {timing.reasoning}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions - DEX: < 2 clicks to value */}
      {(onFindDecisionMakers || onFindTalent) && (
        <div className="px-5 py-3 border-t border-black/5 flex items-center gap-2">
          {onFindDecisionMakers && (
            <button
              onClick={onFindDecisionMakers}
              className="flex-1 py-2.5 bg-[var(--eq-purple)] text-white rounded-xl text-sm font-semibold 
                         hover:bg-[var(--eq-purple)]/90 transition-all duration-700 flex items-center justify-center gap-2"
            >
              <Users className="w-4 h-4" />
              Find Decision Makers
            </button>
          )}
          {onFindTalent && (
            <button
              onClick={onFindTalent}
              className="flex-1 py-2.5 bg-black/5 text-black/70 rounded-xl text-sm font-semibold 
                         hover:bg-black/10 transition-all duration-700 flex items-center justify-center gap-2"
            >
              <Briefcase className="w-4 h-4" />
              Find Talent
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default HiringPredictionCard;
